// pages/public/ComparePage.tsx
/**
 * @description Mobile-first unit comparison page with stacked cards
 * Shows saved units with comparison highlights, differences summary, and clear CTAs
 */
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronUp, Mail, X, Heart, TrendingDown, Maximize2, Clock, FileText, DollarSign, Ruler } from 'lucide-react';
import { useSavedUnits } from '../../context/SavedUnitsContext';
import { formatPrice, formatAvailability } from '../../lib/utils';
import { PublicLayout } from '../../components/layout';
import { BeauxBot, QuickApplicationModal } from '../../components/public';
import { PLACEHOLDER_IMAGES, MOCK_BUILDING } from '../../lib/mockData';
import type { Unit } from '../../types/database';

export function ComparePage() {
  const navigate = useNavigate();
  const { savedUnits, removeUnit, clearAll } = useSavedUnits();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [isApplicationOpen, setIsApplicationOpen] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);

  const handleBack = () => {
    navigate(`/b/${MOCK_BUILDING.slug}`);
  };

  const handleViewUnit = (unitId: string) => {
    navigate(`/b/${MOCK_BUILDING.slug}/unit/${unitId}`);
  };

  const handleContactAll = () => {
    const unitNumbers = savedUnits.map(u => u.unit_number).join(', ');
    window.location.href = `mailto:${MOCK_BUILDING.leasing_email}?subject=Inquiry about Units ${unitNumbers} at ${MOCK_BUILDING.name}&body=Hi, I'm interested in learning more about the following units at ${MOCK_BUILDING.name}:%0D%0A%0D%0A${savedUnits.map(u => `• ${u.unit_type?.name || 'Unit'} #${u.unit_number} - ${formatPrice(u.price)}/mo`).join('%0D%0A')}%0D%0A%0D%0APlease let me know about scheduling viewings.`;
  };

  const handleApplyForUnit = (unit: Unit) => {
    setSelectedUnit(unit);
    setIsApplicationOpen(true);
  };

  // Calculate comparison differences
  const differences = useMemo(() => {
    if (savedUnits.length < 2) return null;

    const prices = savedUnits.map(u => u.price);
    const sizes = savedUnits.map(u => u.square_feet || 0);
    const dates = savedUnits.map(u => new Date(u.availability_date).getTime());

    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    const minSize = Math.min(...sizes.filter(s => s > 0));
    const maxSize = Math.max(...sizes);

    const cheapestIdx = prices.indexOf(minPrice);
    const largestIdx = sizes.indexOf(maxSize);
    const soonestIdx = dates.indexOf(Math.min(...dates));

    return {
      cheapestIdx,
      largestIdx,
      soonestIdx,
      priceDiff: maxPrice - minPrice,
      sizeDiff: maxSize - minSize,
      cheapestUnit: savedUnits[cheapestIdx],
      largestUnit: savedUnits[largestIdx],
    };
  }, [savedUnits]);

  // Calculate comparison highlights for each unit
  const getHighlights = (unit: Unit) => {
    const highlights: { icon: typeof TrendingDown; label: string; color: string }[] = [];

    if (savedUnits.length > 1 && differences) {
      const lowestPrice = Math.min(...savedUnits.map(u => u.price));
      const largestSize = Math.max(...savedUnits.map(u => u.square_feet || 0));
      const earliestDate = Math.min(...savedUnits.map(u => new Date(u.availability_date).getTime()));

      if (unit.price === lowestPrice) {
        highlights.push({ icon: TrendingDown, label: 'Lowest Price', color: 'text-green-600 bg-green-50' });
      }
      if (unit.square_feet && unit.square_feet === largestSize) {
        highlights.push({ icon: Maximize2, label: 'Most Space', color: 'text-blue-600 bg-blue-50' });
      }
      if (new Date(unit.availability_date).getTime() === earliestDate) {
        highlights.push({ icon: Clock, label: 'Available Soonest', color: 'text-amber-600 bg-amber-50' });
      }
    }

    return highlights;
  };

  if (savedUnits.length === 0) {
    return (
      <PublicLayout showFooterPadding={false}>
        <div className="p-4">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
          >
            <ArrowLeft size={20} />
            Back to building
          </button>

          <div className="text-center py-12">
            <Heart size={48} className="mx-auto text-gray-300 mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              No units saved yet
            </h2>
            <p className="text-gray-600 mb-6">
              Save units you like to compare them here
            </p>
            <button
              onClick={handleBack}
              className="px-6 py-3 bg-primary hover:bg-primary-hover text-white rounded-lg font-medium transition-colors"
            >
              Browse Units
            </button>
          </div>
        </div>
        <BeauxBot />
      </PublicLayout>
    );
  }

  return (
    <PublicLayout showFooterPadding={false}>
      <div className="p-4 pb-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft size={20} />
            <span className="font-medium">Back</span>
          </button>

          <button
            onClick={clearAll}
            className="text-sm text-gray-500 hover:text-red-500"
          >
            Clear all
          </button>
        </div>

        <h1 className="text-xl font-semibold text-gray-900 mb-1">
          Compare {savedUnits.length} Units
        </h1>
        <p className="text-sm text-gray-500 mb-4">
          Side-by-side comparison of your saved units
        </p>

        {/* Key Differences Summary */}
        {differences && (
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <TrendingDown size={18} className="text-blue-600" />
              Key Differences
            </h3>
            <ul className="space-y-2 text-sm">
              {differences.priceDiff > 0 && (
                <li className="flex items-center gap-2 text-gray-700">
                  <DollarSign size={16} className="text-green-600" />
                  <span>
                    Price range: <strong>${differences.priceDiff}/mo</strong> difference
                  </span>
                </li>
              )}
              {differences.sizeDiff > 0 && (
                <li className="flex items-center gap-2 text-gray-700">
                  <Ruler size={16} className="text-blue-600" />
                  <span>
                    Size range: <strong>{differences.sizeDiff} sqft</strong> difference
                  </span>
                </li>
              )}
              {differences.cheapestUnit && (
                <li className="flex items-center gap-2 text-gray-700">
                  <TrendingDown size={16} className="text-green-600" />
                  <span>
                    Best value: <strong>{differences.cheapestUnit.unit_type?.name} #{differences.cheapestUnit.unit_number}</strong> at {formatPrice(differences.cheapestUnit.price)}/mo
                  </span>
                </li>
              )}
            </ul>
          </div>
        )}

        {/* Stacked Unit Cards */}
        <div className="space-y-4">
          {savedUnits.map((unit) => {
            const highlights = getHighlights(unit);
            const isExpanded = expandedId === unit.id;

            return (
              <div
                key={unit.id}
                className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm relative"
              >
                {/* Quick Remove Button */}
                <button
                  onClick={() => removeUnit(unit.id)}
                  className="absolute top-3 right-3 p-2 bg-white/90 hover:bg-gray-100 rounded-full shadow-sm z-10"
                  aria-label="Remove from compare"
                >
                  <X size={16} className="text-gray-500" />
                </button>

                {/* Main Card */}
                <div className="p-4">
                  <div className="flex gap-4">
                    {/* Thumbnail */}
                    <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={PLACEHOLDER_IMAGES.livingRoom}
                        alt={unit.unit_type?.name}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0 pr-8">
                      <h3 className="font-semibold text-gray-900 mb-1">
                        {unit.unit_type?.name}
                      </h3>

                      <p className="text-primary font-bold text-lg mb-1">
                        {formatPrice(unit.price)}/mo
                      </p>

                      <div className="text-sm text-gray-500 space-y-0.5">
                        <p>Floor {unit.floor} • Unit {unit.unit_number}</p>
                        {unit.square_feet && <p>{unit.square_feet} sq ft</p>}
                        <p className={formatAvailability(unit.availability_date) === 'Available Now' ? 'text-green-600 font-medium' : ''}>
                          {formatAvailability(unit.availability_date)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Highlight Badges */}
                  {highlights.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {highlights.map((h, i) => (
                        <span
                          key={i}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${h.color}`}
                        >
                          <h.icon size={12} />
                          {h.label}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Expand Toggle */}
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : unit.id)}
                    className="flex items-center justify-center gap-1 w-full mt-3 pt-3 border-t border-gray-100 text-sm text-gray-500 hover:text-gray-700"
                  >
                    {isExpanded ? (
                      <>
                        Less details <ChevronUp size={16} />
                      </>
                    ) : (
                      <>
                        More details <ChevronDown size={16} />
                      </>
                    )}
                  </button>
                </div>

                {/* Expanded Features */}
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-gray-100 bg-gray-50">
                    <div className="pt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Features</h4>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {(unit.features || ['Hardwood floors', 'In-unit laundry', 'Balcony']).map((feature, i) => (
                          <span
                            key={i}
                            className="px-2 py-1 bg-white border border-gray-200 rounded-full text-xs text-gray-600"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => handleViewUnit(unit.id)}
                          className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium text-sm hover:bg-gray-100 transition-colors"
                        >
                          View Full Details
                        </button>
                        <button
                          onClick={() => handleApplyForUnit(unit)}
                          className="flex-1 py-2.5 bg-primary text-white rounded-lg font-medium text-sm hover:bg-primary-hover transition-colors"
                        >
                          Contact
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Action CTAs */}
        <div className="mt-8 space-y-3">
          {differences?.cheapestUnit && (
            <button
              onClick={() => handleApplyForUnit(differences.cheapestUnit)}
              className="w-full py-4 bg-primary hover:bg-primary-hover text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors shadow-sm"
            >
              <FileText size={18} />
              Contact About Best Value ({formatPrice(differences.cheapestUnit.price)}/mo)
            </button>
          )}

          <button
            onClick={handleContactAll}
            className="w-full py-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium flex items-center justify-center gap-2 transition-colors"
          >
            <Mail size={18} />
            Contact Agent About All {savedUnits.length} Units
          </button>

          <p className="text-xs text-gray-500 text-center">
            The agent can help you compare and schedule viewings
          </p>
        </div>
      </div>

      {/* Application Modal */}
      <QuickApplicationModal
        isOpen={isApplicationOpen}
        onClose={() => {
          setIsApplicationOpen(false);
          setSelectedUnit(null);
        }}
        building={{
          id: 'compare',
          name: MOCK_BUILDING.name,
          address: MOCK_BUILDING.address,
          email: MOCK_BUILDING.leasing_email,
        }}
        unit={selectedUnit ? {
          type: selectedUnit.unit_type?.name || 'Unit',
          price: selectedUnit.price,
          floor: selectedUnit.floor,
        } : undefined}
      />

      <BeauxBot />
    </PublicLayout>
  );
}
